import { useSearchParams } from 'react-router-dom';
import { Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { HelpButton } from '../../../app/guides/HelpButton';
import { TabHelp } from '../../../app/guides/TabHelp';
import { useT } from '../../../i18n/I18nProvider';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { useTicketSettings } from '../lib/data';
import { SettingsForm } from './General';
import { TypesSection } from './Types';
import { PrioritiesSection, SlaSection, StatusesSection } from './Lists';
import { RulesSection } from './Rules';
import '../tickets.css';

const SECTIONS = ['general', 'updates', 'satisfaction', 'types', 'statuses', 'priorities', 'sla', 'automations'] as const;
type Section = (typeof SECTIONS)[number];

/** The "show me" links each tab's question mark offers (8 October 2026). */
const TAB_LINKS: Partial<Record<Section, { labelKey: string; to?: string; tour?: string }[]>> = {
  types: [{ labelKey: 'guide.tickets.link.howTypes', tour: 'types' }],
  sla: [{ labelKey: 'guide.tickets.link.howSla', tour: 'sla' }, { labelKey: 'guide.tickets.link.hours', to: '/settings/organisation' }],
  automations: [{ labelKey: 'guide.tickets.link.howAutomation', tour: 'automation' }],
  general: [{ labelKey: 'guide.tickets.link.teams', to: '/settings/team' }]
};

export default function TicketSettingsPage() {
  const t = useT();
  const canEdit = useIsOrgAdmin();
  const [params, setParams] = useSearchParams();
  const raw = params.get('section');
  const section: Section = (SECTIONS as readonly string[]).includes(raw ?? '') ? (raw as Section) : 'general';
  const settingsQ = useTicketSettings();
  const limits = settingsQ.data?.data.limits;

  function choose(next: string) {
    const p = new URLSearchParams(params);
    p.set('section', next);
    setParams(p, { replace: true });
  }

  return (
    <Stack gap="lg">
      <PageHeader title={t('tickets.settings.title')} description={t('tickets.settings.description')} withDivider={false}
        actions={<HelpButton guide="tickets" />} />
      {!canEdit && <Alert color="info">{t('tickets.readOnly')}</Alert>}

      <div data-guide="tickets-settings-tabs">
        <Tabs value={section} onChange={choose}>
          <Tabs.List>
            {SECTIONS.map((s) => (
              <span key={s} className="rr-tabgroup">
                <Tabs.Tab value={s}>{t(`tickets.settings.section.${s}`)}</Tabs.Tab>
                <TabHelp guide="tickets" titleKey={`tickets.settings.section.${s}`} bodyKey={`guide.tickets.tab.${s}`} links={TAB_LINKS[s]} />
              </span>
            ))}
          </Tabs.List>
        </Tabs>
      </div>

      <div data-guide={`tickets-settings-${section}`}>

      {(section === 'general' || section === 'updates' || section === 'satisfaction') && <SettingsForm section={section} canEdit={canEdit} />}
      {section === 'types' && <TypesSection canEdit={canEdit} limit={limits?.ticket_types} />}
      {section === 'statuses' && <StatusesSection canEdit={canEdit} />}
      {section === 'priorities' && <PrioritiesSection canEdit={canEdit} />}
      {section === 'sla' && <SlaSection canEdit={canEdit} />}
      {section === 'automations' && <RulesSection canEdit={canEdit} limit={limits?.ticket_automations} />}
      </div>

      {section === 'general' && limits && (
        <Card withBorder>
          <Stack gap="xs">
            <Text role="subtitle" fw={600}>{t('tickets.settings.limits')}</Text>
            <Group gap="lg" wrap="wrap">
              <Text role="bodySmall">
                {limits.ticket_types == null ? t('tickets.settings.limitTypesNone') : t('tickets.settings.limitTypes', { n: limits.ticket_types })}
              </Text>
              <Text role="bodySmall">
                {limits.ticket_automations == null ? t('tickets.settings.limitRulesNone') : t('tickets.settings.limitRules', { n: limits.ticket_automations })}
              </Text>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('tickets.settings.limitsHint')}</Text>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
